import {getLastReferences, getLastUserMessage, getReferenceDifferences} from "../../../misc/messageMetadataUtils.ts";
import messageIdMapping from "../../../IdMappings/MessageIdMapping.ts";
import {addFilesToTracker} from "../../../db/vectorDb/addFilesToTrackers.ts";
import {updateToolCall} from "../../../llm/contextManagement/toolCallUpdater.ts";
import {truncateToolMessagesByFilePath} from "../../../llm/contextManagement/messageTrucater.ts";


export const handleGiveInitialFiles = (data: any, setDynamicStateObj: any, environmentObjRef: any, userObjRef: any) => {
    const paths: string[] = [];
    const initialFiles = data
        .map((item: { value: any; }) => {
            const filePath = item.value;
            // Track the file while we're mapping
            paths.push(filePath);
            return {
                filePath: filePath,
                isSelected: false
            };
        })
        .sort((a: any, b: any) => (a.filePath.split('/').pop() || '').localeCompare(b.filePath.split('/').pop() || ''));

    if (paths.length > 0) addFilesToTracker(userObjRef, environmentObjRef, paths);

    const uniqueStatuses = Array.from(new Set(initialFiles.map((status: any) => status.filePath)))
        .map(filePath => initialFiles.find((status: any) => status.filePath === filePath));

    setDynamicStateObj({openFileStatuses: uniqueStatuses});
};

export const handleUpdateFileOpenStatus = (data: any, dynamicStateObjRef: any, setDynamicStateObj: any) => {
    const filePath = data[0];
    const status = data[1];
    let updatedStatuses: any;

    if (status === "opened") {
        updatedStatuses = [...dynamicStateObjRef.current.openFileStatuses, {
            filePath: filePath,
            isSelected: false
        }].sort((a: any, b: any) => (a.filePath.split('/').pop() || '').localeCompare(b.filePath.split('/').pop() || ''));
    } else if (status === "closed") {
        updatedStatuses = dynamicStateObjRef.current.openFileStatuses
            .filter((status: any) => status.filePath !== filePath)
            .sort((a: any, b: any) => (a.filePath.split('/').pop() || '').localeCompare(b.filePath.split('/').pop() || ''));
    }
    const uniqueStatuses = Array.from(new Set(updatedStatuses.map((status: any) => status.filePath)))
        .map(filePath => updatedStatuses.find((status: any) => status.filePath === filePath));

    setDynamicStateObj({openFileStatuses: uniqueStatuses});
};

export const formatFilesMessage = (openFileContents: any, focusedFileContents: any, currentMessageRef: any, fileErrors: string, llmRequestRefs: any) => {
    const currentRefVals = [];
    const lastUserMessage = getLastUserMessage(currentMessageRef);

    let hasFileOpen = false;
    let filesMessage = "Here is the updated state of the files I have open:\n";
    let truncatedFilesMessage = `${filesMessage}...\n<Truncated for brevity>\n...`;
    if (openFileContents) {
        hasFileOpen = true;
        for (let i = 0; i < openFileContents.length; i++) {
            const fileContent = openFileContents[i];

            currentRefVals.push({path: fileContent.fullPath, type: 'file'});
            llmRequestRefs.current.additionalContext.mostRecentFileStates[fileContent.fullPath] = {languageName: fileContent.languageName, fileContents: fileContent.fileContents};
            filesMessage += `File ${i + 1}: ${fileContent.fullPath}`;
            filesMessage += `\nFile Contents:\n\`\`\`${fileContent.languageName}\n${fileContent.fileContents}\n\`\`\`\n\n`;

            llmRequestRefs.current.additionalContext.filesAlreadySeen.add(fileContent.fullPath);
        }
    }
    if (focusedFileContents) {
        hasFileOpen = true;

        if (!currentRefVals.some((item: any) => item.path === focusedFileContents.focusedFilePath)) {
            currentRefVals.push({path: focusedFileContents.focusedFilePath, type: 'file'});
        }

        if (focusedFileContents.focusedLineRange) {
            if (focusedFileContents.focusedLineContents) {
                lastUserMessage.metadata.focusedLineContents = {
                    filePath: focusedFileContents.focusedFilePath,
                    content: focusedFileContents.focusedLineContents,
                    language: focusedFileContents.focusedLineLanguageName || 'plaintext'
                };
            }
            filesMessage += `I currently have the file ${focusedFileContents.focusedFilePath} focused, right around the lines ${focusedFileContents.focusedLineRange}`
        }
    }

    if (fileErrors.trim() !== "") {
        filesMessage += `\n\nI have the following errors:\n"""\n${fileErrors}\n"""`
    }

    const lastReferences = getLastReferences(messageIdMapping[lastUserMessage.parent]);

    const [ onlyInLast, onlyInCurrent ] = getReferenceDifferences(lastReferences, currentRefVals);
    if (onlyInCurrent.length !== 0 || onlyInLast.length !== 0) {
        lastUserMessage.metadata.referenceChanges = { added: onlyInCurrent, removed: onlyInLast };
    }

    if (hasFileOpen) {
        lastUserMessage.metadata.references = currentRefVals;
        return {original: filesMessage, truncated: truncatedFilesMessage};
    }

    else return {original: null, truncated: null};
};

export const formatFilesForFilterer = (fileContents: any[]) => {
    const filesFromFirstPass = [];

    if (fileContents) {
        for (let i = 0; i < fileContents.length; i++) {
            const fileContent = fileContents[i];
            const filesMessage = `\`\`${fileContent.languageName}\n${fileContent.fileContents}\n\`\`\``;
            filesFromFirstPass.push(filesMessage);
        }
    }

    return filesFromFirstPass;
}

export const formatSearchCodebaseResults = (fileContents: any[], llmRequestRefs: any, setMessages: any, toolCallId: string) => {
    let filesMessage = "";
    const returnedFileNames = [];
    const alreadySeenFiles = [];
    const newFiles = [];

    if (fileContents) {
        // Truncate previous tool calls that showed files we're about to return
        const filePaths = fileContents.map((fc: any) => fc.fullPath);

        // Truncate previous tool calls (openFile, searchCodebase) for each file path
        filePaths.forEach((fp: string) => truncateToolMessagesByFilePath(llmRequestRefs.current.toolMessages, fp));

        for (let i = 0; i < fileContents.length; i++) {
            const fileContent = fileContents[i];

            filesMessage += `File ${i + 1}: ${fileContent.fullPath}`;

            // Always return full file contents, truncation of previous tool calls handles context management
            filesMessage += `\nFile Contents:\n\`\`\`${fileContent.languageName}\n${fileContent.fileContents}\n\`\`\`\n\n`;

            if (llmRequestRefs.current.additionalContext.filesAlreadySeen.has(fileContent.fullPath)) {
                alreadySeenFiles.push(fileContent.fullPath);
            } else {
                newFiles.push(fileContent.fullPath);
            }

            returnedFileNames.push(fileContent.fullPath);
            llmRequestRefs.current.additionalContext.filesAlreadySeen.add(fileContent.fullPath);
            llmRequestRefs.current.additionalContext.mostRecentFileStates[fileContent.fullPath] = {languageName: fileContent.languageName, fileContents: fileContent.fileContents};
        }
    }

    updateToolCall(llmRequestRefs, toolCallId, { returnedFileNames, alreadySeenFiles, newFiles });
    setMessages((prev: any) => prev.deepCopy())

    return filesMessage.trim() === "" ? "No matches found!" : filesMessage;
};



export const formatReviewChangesResults = (diffResults: any) => {
    const formattedFiles = [];
    let textMessage = "";

    for (const filePath in diffResults) {
        const diffData = diffResults[filePath];
        const fileResult: any = {
            filePath,
            operation: 'edit',
            originalFilePath: undefined,
            changes: []
        };

        // Check if this is an operation object or a diff array
        if (typeof diffData === 'object' && !Array.isArray(diffData)) {
            // Handle operation objects
            fileResult.operation = diffData.operation;

            if (diffData.operation === 'create') {
                fileResult.content = diffData.content;
                textMessage += `${filePath} was created:\n\`\`\`\n${diffData.content || ""}\n\`\`\`\n\n`;
            } else if (diffData.operation === 'delete') {
                textMessage += `${filePath} was deleted\n\n`;
            } else if (diffData.operation === 'move') {
                fileResult.originalFilePath = diffData.originalFilePath;
                textMessage += `${diffData.originalFilePath} was moved to ${filePath}\n\n`;
            } else {
                textMessage += `${filePath} was ${diffData.operation}d\n\n`;
            }
        }
        else if (Array.isArray(diffData)) {
            // Process diff array for edited files
            const diffParts = diffData;
            let addedLines = 0;
            let removedLines = 0;
            const chunks = [];

            // Find all change chunks
            let lineNumber = 1;
            for (let i = 0; i < diffParts.length; i++) {
                const part = diffParts[i];

                if (part.added || part.removed) {
                    // Count lines
                    if (part.added) addedLines += part.value.length;
                    if (part.removed) removedLines += part.value.length;

                    // Get context
                    const contextBefore = [];
                    const contextAfter = [];
                    let contextBeforeStartLine = lineNumber;

                    // Get context before (2 lines)
                    for (let j = i - 1; j >= 0 && contextBefore.length < 2; j--) {
                        if (!diffParts[j].added && !diffParts[j].removed) {
                            const lines = diffParts[j].value;
                            const startIdx = Math.max(0, lines.length - 2);
                            contextBefore.unshift(...lines.slice(startIdx));
                            contextBeforeStartLine = lineNumber - (lines.length - startIdx);
                            break;
                        }
                    }

                    // Get context after (2 lines)
                    let contextAfterStartLine = lineNumber;
                    if (!part.removed) contextAfterStartLine += part.value.length;

                    for (let j = i + 1; j < diffParts.length && contextAfter.length < 2; j++) {
                        if (!diffParts[j].added && !diffParts[j].removed) {
                            const lines = diffParts[j].value;
                            contextAfter.push(...lines.slice(0, 2));
                            break;
                        }
                    }

                    chunks.push({
                        type: part.added ? 'add' : 'remove',
                        lines: part.value,
                        startLine: part.removed ? lineNumber : lineNumber,
                        contextBefore,
                        contextBeforeStartLine: contextBeforeStartLine,
                        contextAfter,
                        contextAfterStartLine: contextAfterStartLine
                    });
                }

                // Update line number for non-added parts
                if (!part.added) {
                    lineNumber += part.value.length;
                }
            }

            fileResult.addedLines = addedLines;
            fileResult.removedLines = removedLines;
            fileResult.changes = chunks;

            // Build text representation
            if (chunks.length > 0) {
                textMessage += `${filePath} was edited:\n`;
                textMessage += `+${addedLines} -${removedLines} lines\n\n`;

                for (const chunk of chunks) {
                    let lineCounter: number;
                    if (chunk.type === 'remove') {
                        textMessage += "---\n";
                        lineCounter = chunk.contextBeforeStartLine;
                        chunk.contextBefore.forEach(line => {
                            textMessage += `${lineCounter}: ${line}\n`;
                            lineCounter++;
                        });
                        let removeLineCounter = chunk.startLine;
                        chunk.lines.forEach((line: string) => {
                            textMessage += `${removeLineCounter}: -${line}\n`;
                            removeLineCounter++;
                        });
                        lineCounter = chunk.contextAfterStartLine;
                        chunk.contextAfter.forEach(line => {
                            textMessage += `${lineCounter}: ${line}\n`;
                            lineCounter++;
                        });
                        textMessage += "\n";
                    } else {
                        textMessage += "+++\n";
                        lineCounter = chunk.contextBeforeStartLine;
                        chunk.contextBefore.forEach(line => {
                            textMessage += `${lineCounter}: ${line}\n`;
                            lineCounter++;
                        });
                        let addLineCounter = chunk.startLine;
                        chunk.lines.forEach((line: string) => {
                            textMessage += `${addLineCounter}: +${line}\n`;
                            addLineCounter++;
                        });
                        lineCounter = chunk.contextAfterStartLine;
                        chunk.contextAfter.forEach(line => {
                            textMessage += `${lineCounter}: ${line}\n`;
                            lineCounter++;
                        });
                        textMessage += "\n";
                    }
                }
            } else {
                textMessage += `**${filePath}**: No changes detected\n\n`;
            }
        }

        formattedFiles.push(fileResult);
    }

    return {
        files: formattedFiles,
        textMessage: textMessage.trim() || "<No changes found>"
    };
};